import {
    faCheckCircle,
    faChevronDown,
    faChevronUp,
    faCircle,
    faHospital,
    faHourglassStart,
    faRefresh
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Col, Container, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { callDashboardLLM, callGetDashboardLLMStatus } from "../../../amplify/appsync/dashboard-llm";
import { callQuery, LLM_VISIT_FEEDBACK_BY_CSN, LLMVisitFeedback, PatientFileVersion } from "../../../amplify/appsync/query";
import { PatientVisit } from "../../../amplify/appsync/search-patients";
import { callWrite, EXPLAIN_REVIEW_ADMIT_DC, markJobAsViewed, SCAN_ADMIT_DC, UPDATE_LAST_VIEWED, UPDATE_LLM_VISIT_FEEDBACK } from "../../../amplify/appsync/write";
import { GenerateModuleHeroV2 } from "../../common/GenerateModuleHeroV2";
import { dashboardLLMCache } from "../../dashboard/llm/cache";
import { FakeLoadingBar } from "../../dashboard/llm/common/FakeLoadingBar";
import { LLMDashboardUpdated } from "../../dashboard/llm/common/LLMDashboardUpdated";
import { sleep } from "../../dashboard/llm/common/utils";
import { isSectionUnread } from "../../dashboard/llm/utils";
import { DISPOSITION_MAPPING } from "../../ops/metricsPlotOpts";
import CustomSurveyForm, { getCombinedSectionToRate } from "./CustomSurveyForm";
import { NotableLabelsBadge } from "../../common/NotableLabelsBadge";
import { getNotableLabels, isFieldValueNotable } from "../../../utils/notableLabels";

interface DashboardProps {
    patientVisit?: PatientVisit;
    statuses: any;
    setStatuses: any;
    latestFileVersions: any;
    handleFileVersionChange: any;
    unreadFileVersionMap?: Record<string, PatientFileVersion>;
    className?: string;
    isQuickView?: boolean;
    tenant?: string;
    surveyResultsUpdateCallback?: (csn: string, results: Record<string, any>) => void;
    notableLabelsUpdateCallback?: (csn: string, surveyType: string, count: number) => void;
}

const EXPECTED_REFRESH_TIME = 120_000;

export const FEEDBACK_TYPE_SUMMARY = "cohort_admit_summary_ed";
export const FEEDBACK_TYPE_DIAGNOSTIC_UNCERTAINTY = "cohort_admit_diagnostic_uncertainty_ed";
export const FEEDBACK_TYPE_DIAGNOSTIC_UNCERTAINTY_JUSTIFICATION = "cohort_admit_diagnostic_uncertainty_justification";
export const FEEDBACK_TYPE_SUMMARY_ADMIT = "cohort_admit_summary_admit";
export const FEEDBACK_TYPE_DIAGNOSES_ADMIT = "cohort_admit_diagnoses_admit";
export const FEEDBACK_TYPE_PATIENT_SUMMARY_ADMIT = "cohort_admit_patient_summary_admit";
export const FEEDBACK_TYPE_APPROPRIATE_LOC_PROSPECTIVE = "cohort_admit_appropriate_loc_prospective";
export const FEEDBACK_TYPE_APPROPRIATE_LOC_PROSPECTIVE_JUSTIFICATION = "cohort_admit_appropriate_loc_prospective_justification";
export const FEEDBACK_TYPE_APPROPRIATE_LOC_RETROSPECTIVE = "cohort_admit_appropriate_loc_retrospective";
export const FEEDBACK_TYPE_APPROPRIATE_LOC_RETROSPECTIVE_JUSTIFICATION = "cohort_admit_appropriate_loc_retrospective_justification";
export const FEEDBACK_TYPE_SIGNIFICANT_FINDINGS = "cohort_admit_significant_findings";
export const FEEDBACK_TYPE_SIGNIFICANT_FINDINGS_JUSTIFICATION = "cohort_admit_significant_findings_justification";
export const FEEDBACK_TYPE_DIAGNOSTIC_CLARITY = "cohort_admit_diagnostic_clarity";
export const FEEDBACK_TYPE_DIAGNOSTIC_CLARITY_JUSTIFICATION = "cohort_admit_diagnostic_clarity_justification";
export const FEEDBACK_TYPE_VALUE_OF_STAY = "cohort_admit_value_of_stay";
export const FEEDBACK_TYPE_VALUE_OF_STAY_JUSTIFICATION = "cohort_admit_value_of_stay_justification";
export const FEEDBACK_TYPE_MODIFIABLE_ADMISSION = "cohort_admit_modifiable_admission";
export const FEEDBACK_TYPE_MODIFIABLE_ADMISSION_JUSTIFICATION = "cohort_admit_modifiable_admission_justification";
export const FEEDBACK_TYPE_CDS_OPPORTUNITY = "cohort_admit_cds_opportunity";
export const FEEDBACK_TYPE_CDS_OPPORTUNITY_DETAIL = "cohort_admit_cds_opportunity_detail";
export const FEEDBACK_TYPE_STREAMLINE_ADMISSION = "cohort_admit_streamline_admission";
export const FEEDBACK_TYPE_STREAMLINE_ADMISSION_JUSTIFICATION = "cohort_admit_streamline_admission_justification";
export const FEEDBACK_TYPE_LONGITUDINAL_CARE_INSIGHT_ADMIT = "cohort_admit_longitudinal_care_insight_admit";
export const FEEDBACK_TYPE_THERAPEUTIC_PIVOT_ED = "cohort_admit_therapeutic_pivot_ed";
export const FEEDBACK_TYPE_THERAPEUTIC_PIVOT_JUSTIFICATION_ED = "cohort_admit_therapeutic_pivot_justification_ed";
export const FEEDBACK_TYPE_VALUE_OF_CARE_ED = "cohort_admit_value_of_care_ed";
export const FEEDBACK_TYPE_VALUE_OF_CARE_JUSTIFICATION_ED = "cohort_admit_value_of_care_justification_ed";
export const FEEDBACK_TYPE_THERAPEUTIC_PIVOT_IP = "cohort_admit_therapeutic_pivot_ip";
export const FEEDBACK_TYPE_THERAPEUTIC_PIVOT_JUSTIFICATION_IP = "cohort_admit_therapeutic_pivot_justification_ip";
export const FEEDBACK_TYPE_VALUE_OF_CARE_IP = "cohort_admit_value_of_care_ip";
export const FEEDBACK_TYPE_VALUE_OF_CARE_JUSTIFICATION_IP = "cohort_admit_value_of_care_justification_ip";
export const FEEDBACK_TYPE_ASTUTE_DIAGNOSIS_ADMIT = "cohort_admit_astute_diagnosis_admit";
export const FEEDBACK_TYPE_ASTUTE_DIAGNOSIS_JUSTIFICATION_ADMIT = "cohort_admit_astute_diagnosis_justification_admit";

const REQUIRED_FEEDBACK_TYPES = [
    FEEDBACK_TYPE_SUMMARY,
    FEEDBACK_TYPE_DIAGNOSTIC_UNCERTAINTY,
    FEEDBACK_TYPE_DIAGNOSTIC_UNCERTAINTY_JUSTIFICATION,
    FEEDBACK_TYPE_SUMMARY_ADMIT,
    FEEDBACK_TYPE_DIAGNOSES_ADMIT,
    FEEDBACK_TYPE_PATIENT_SUMMARY_ADMIT,
    FEEDBACK_TYPE_APPROPRIATE_LOC_PROSPECTIVE,
    FEEDBACK_TYPE_APPROPRIATE_LOC_PROSPECTIVE_JUSTIFICATION,
    FEEDBACK_TYPE_APPROPRIATE_LOC_RETROSPECTIVE,
    FEEDBACK_TYPE_APPROPRIATE_LOC_RETROSPECTIVE_JUSTIFICATION,
    FEEDBACK_TYPE_SIGNIFICANT_FINDINGS,
    FEEDBACK_TYPE_SIGNIFICANT_FINDINGS_JUSTIFICATION,
    FEEDBACK_TYPE_DIAGNOSTIC_CLARITY,
    FEEDBACK_TYPE_DIAGNOSTIC_CLARITY_JUSTIFICATION,
    FEEDBACK_TYPE_VALUE_OF_STAY,
    FEEDBACK_TYPE_VALUE_OF_STAY_JUSTIFICATION,
    FEEDBACK_TYPE_MODIFIABLE_ADMISSION,
    FEEDBACK_TYPE_MODIFIABLE_ADMISSION_JUSTIFICATION,
    FEEDBACK_TYPE_CDS_OPPORTUNITY,
    FEEDBACK_TYPE_CDS_OPPORTUNITY_DETAIL,
    FEEDBACK_TYPE_STREAMLINE_ADMISSION,
    FEEDBACK_TYPE_STREAMLINE_ADMISSION_JUSTIFICATION,
    FEEDBACK_TYPE_LONGITUDINAL_CARE_INSIGHT_ADMIT,
    FEEDBACK_TYPE_THERAPEUTIC_PIVOT_ED,
    FEEDBACK_TYPE_THERAPEUTIC_PIVOT_JUSTIFICATION_ED,
    FEEDBACK_TYPE_VALUE_OF_CARE_ED,
    FEEDBACK_TYPE_VALUE_OF_CARE_JUSTIFICATION_ED,
    FEEDBACK_TYPE_THERAPEUTIC_PIVOT_IP,
    FEEDBACK_TYPE_THERAPEUTIC_PIVOT_JUSTIFICATION_IP,
    FEEDBACK_TYPE_VALUE_OF_CARE_IP,
    FEEDBACK_TYPE_VALUE_OF_CARE_JUSTIFICATION_IP,
    FEEDBACK_TYPE_ASTUTE_DIAGNOSIS_ADMIT,
    FEEDBACK_TYPE_ASTUTE_DIAGNOSIS_JUSTIFICATION_ADMIT,
] as const;

const ED_SUMMARY_OPTION_ALIASES: Record<string, string> = {
    'Accurate': 'Good',
    'Partially accurate': 'Incomplete',
    'Inaccurate': 'Poor',
};

const isSurveyCompleted = (feedbackTargetToSurvey: any) => {
    return REQUIRED_FEEDBACK_TYPES.every(type => feedbackTargetToSurvey[type]);
};


export const ExplainAdmitDCDashboard = ({
    patientVisit,
    statuses,
    setStatuses,
    unreadFileVersionMap = undefined,
    latestFileVersions = undefined,
    handleFileVersionChange = undefined,
    className,
    isQuickView,
    tenant = 'stanford',
    surveyResultsUpdateCallback,
    notableLabelsUpdateCallback,
}: DashboardProps) => {

    const [isLoading, setIsLoading] = useState(false);
    const [scanAdmitData, setScanAdmitData] = useState<any>();
    const [explainReviewAdmit, setExplainReviewAdmit] = useState<any>();
    const [feedbackTargetToSurvey, setFeedbackTargetToSurvey] = useState<any>({});
    const [errorMessage, setErrorMessage] = useState("");
    const [isExpanded, setIsExpanded] = useState(true);
    const [manuallyChangedIsExpanded, setManuallyChangedIsExpanded] = useState(false);

    const sectionToSetter = {
        [EXPLAIN_REVIEW_ADMIT_DC]: setExplainReviewAdmit,
    }

    // Function to fetch dashboard data
    const fetchDashboardData = async (patient: PatientVisit) => {
        setIsLoading(true);
        const decoder = new TextDecoder();
        
        try {
            // We will render the data just like any other dashboard
            const cachedScanData = await dashboardLLMCache.get(SCAN_ADMIT_DC, patient, tenant);
            if (cachedScanData) {
                setScanAdmitData(cachedScanData);
            } else {
                setScanAdmitData(undefined);
            }

            const cachedData = await dashboardLLMCache.get(EXPLAIN_REVIEW_ADMIT_DC, patient, tenant);
            if (cachedData) {
                // Found cached data
                setExplainReviewAdmit(cachedData);
                markJobAsViewed(EXPLAIN_REVIEW_ADMIT_DC, patientVisit);
                // Update notable labels count
                if (notableLabelsUpdateCallback && patientVisit) {
                    const notableCount = getNotableLabels('explain_review_admit_dc', cachedData).length;
                    notableLabelsUpdateCallback(patientVisit.csn, 'explain_review_admit_dc', notableCount);
                }
            } else {
                setExplainReviewAdmit(undefined);
            }
            setManuallyChangedIsExpanded(false);

            // Fetch survey data
            await fetchSurveyData();
        } catch (error) {
            console.error("Error fetching data:", error);
            setErrorMessage("Could not load data. Check that you are on the Stanford network and if using VPN that your VPN Group is set to either 'CardinalKey-Full' or 'Stanford-Full'");
            setScanAdmitData(undefined);
            setExplainReviewAdmit(undefined);
        } finally {
            setIsLoading(false);
        }
    };
    
    // Function to fetch survey data
    const fetchSurveyData = async () => {
        if (!patientVisit) {
            return;
        }

        try {
            console.log("Fetching survey data for CSN:", patientVisit.csn);
            const feedbackItems = await callQuery({
                operation: LLM_VISIT_FEEDBACK_BY_CSN,
                csn: patientVisit.csn,
            }) as LLMVisitFeedback[];
            
            console.log("Survey data result:", feedbackItems);
            
            if (feedbackItems && feedbackItems.length > 0) {
                // Get all original survey feedback items
                const originalSurveyFeedbackItems = feedbackItems.filter(
                    (item: LLMVisitFeedback) => 
                        item.feedback_type === 'survey' && 
                        (item.feedback_target.startsWith("cohort_admit_"))
                );

                const feedbackTargetToSurvey: any = {};

                for (let item of originalSurveyFeedbackItems) {
                    const survey = JSON.parse(item.note);
                    feedbackTargetToSurvey[item.feedback_target] = {
                        "option": survey.option,
                        "options": survey.options,
                        "comment": survey.comment,
                    };
                }

                if (surveyResultsUpdateCallback) {
                    surveyResultsUpdateCallback(patientVisit.csn, feedbackTargetToSurvey);
                }
                setFeedbackTargetToSurvey(feedbackTargetToSurvey);
            } else {
                setFeedbackTargetToSurvey({});
            }
        } catch (error) {
            console.error("Error fetching survey data:", error);
        }
    };

    const handleSave = async (feedback_target: string, data: any): Promise<boolean> => {
        if (!patientVisit) {
            return false;
        }

        // Set this flag to prevent section from collapsing when user finishes survey
        setManuallyChangedIsExpanded(true);

        try {
            const response = await callWrite({
                operation: UPDATE_LLM_VISIT_FEEDBACK,
                creation_time: new Date().getTime(),
                csn: patientVisit.csn,
                mrn: '',
                feedback_target: feedback_target,
                feedback_type: 'survey',
                note: JSON.stringify(data),
            });

            // Check if response is empty or has an error
            if (!response || Object.keys(response).length === 0) {
                window.alert("Failed to submit survey. Please refresh and try again.");
                return false;
            }

            // Refresh the survey data after submission
            await fetchSurveyData();

            return true;
        } catch (error) {
            console.error("Error submitting original survey:", error);

            // Show alert with error details
            if (error instanceof Error) {
                window.alert(`Failed to submit survey: ${error.message}`);
            } else {
                window.alert("Failed to submit survey. An unknown error occurred.");
            }

            return false;
        }
    };

    const getIsCompleted = () => {
        const isCompleted = isSurveyCompleted(feedbackTargetToSurvey);

        return (
            <Row>
                <Col>
                    {
                        isCompleted ? (
                            <Alert variant="success">
                                <span className="me-1"><FontAwesomeIcon icon={faCheckCircle} /></span> Survey Completed
                            </Alert>
                        ) : (
                            <Alert variant="info">
                                <span className="me-1"><FontAwesomeIcon icon={faHourglassStart} /></span> Survey Not Yet Completed
                            </Alert>
                        )
                    }
                </Col>
            </Row>
        );
    };

    const getSectionHeader = (title: string) => {
        return (
            <Row>
                <Col>
                    <div className="survey-section p-3 mb-4">
                        {title}
                    </div>
                </Col>
            </Row>
        );
    };

    const getContextSection = (title: string, data: string | string[], horizontalLineAbove: boolean = true) => {
        return (
            <>
                {horizontalLineAbove && <hr />}
                <Row className="mb-2">
                    <Col>
                        <strong>{title}</strong>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        {Array.isArray(data) ? (
                            <div className="survey-context">
                                <ul>
                                    {data.map((item, index) => (
                                        <li key={index}>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : (
                            <div className="survey-context">
                                {data}
                            </div>
                        )}
                    </Col>
                </Row>
            </>
        );
    }

    const getSectionToRate = (title: string, data: string | string[], surveyTitle: string,
        surveyDescription: string | undefined, surveyOptions: string[],
        surveyKey: string, isMultiSelect?: boolean, optionToValueMap?: Record<string, string>, useSegmentedButtons?: boolean,
        customLeftHeader?: string, customRightHeader?: string) => {
        return (
            <>
                {/* Question Header spanning both columns with box */}
                <Row className="mt-3">
                    <Col>
                        <div className="survey-question-header mb-2">
                            <div>
                                {title}
                            </div>
                            {surveyDescription && (
                                <div className="small mt-1" style={{fontWeight: 'normal'}}>
                                    {surveyDescription}
                                </div>
                            )}
                        </div>
                    </Col>
                </Row>
                {/* Column Headers */}
                <Row className="mb-2">
                    <Col>
                        <div className="text-muted small">
                            <strong>{customLeftHeader || "Capacity Assessment"}</strong>
                        </div>
                    </Col>
                    <Col>
                        <div className="text-muted small">
                            <strong>{customRightHeader || "MD Assessment"}</strong>
                        </div>
                    </Col>
                </Row>
                {/* Content Row */}
                <Row>
                    <Col>
                        {Array.isArray(data) ? (
                            <div className="survey-context">
                                <ul>
                                    {data.map((item, index) => (
                                        <li key={index}>
                                            {(optionToValueMap && optionToValueMap[item]) ? optionToValueMap[item] : item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : (
                            <div className="survey-context">
                                {(optionToValueMap && optionToValueMap[data]) ? optionToValueMap[data] : data}
                            </div>
                        )}
                    </Col>
                    <Col>
                        <CustomSurveyForm
                            title={surveyTitle}
                            altTitle={title}
                            description={undefined}
                            options={surveyOptions}
                            onSubmit={(data) => handleSave(surveyKey, data)}
                            enableComment={true}
                            initialSelectedOption={feedbackTargetToSurvey[surveyKey] ? feedbackTargetToSurvey[surveyKey].option : undefined}
                            initialSelectedOptions={feedbackTargetToSurvey[surveyKey] ? feedbackTargetToSurvey[surveyKey].options : undefined}
                            initialComment={feedbackTargetToSurvey[surveyKey] ? feedbackTargetToSurvey[surveyKey].comment : undefined}
                            isMultiSelect={isMultiSelect}
                            optionToValueMap={optionToValueMap}
                            useSegmentedButtons={useSegmentedButtons}
                        />
                    </Col>
                </Row>
            </>
        )
    }

    const flattenAndFormatNotes = (notes: any) => {
        let output = "";
        if (notes) {
            for (let k of Object.keys(notes)) {
                for (let note of notes[k]) {
                    output += `NOTE ${(note.note_text)}\n`
                    output += note.note_text + "\n\n";
                }
            }
        }
        return output;
    }

    // Update useEffect to also fetch patient visit data
    useEffect(() => {
        if (patientVisit) {
            fetchDashboardData(patientVisit);
        }
    }, [patientVisit, tenant]);

    useEffect(() => {
        if (isQuickView && !manuallyChangedIsExpanded) {
            if (isSurveyCompleted(feedbackTargetToSurvey)) {
                setIsExpanded(false);
            } else {
                setIsExpanded(true);
            }
        }
    }, [feedbackTargetToSurvey]);

    const refresh = (refresh: boolean) => {
        if (!patientVisit) {
            return;
        }
        setIsLoading(true);
        callDashboardLLM({
            "operation": SCAN_ADMIT_DC,
            "mrn": patientVisit.mrn,
            "csn": patientVisit.csn,
        }).then((scanRevisitResponse) => {
            setScanAdmitData(scanRevisitResponse);

            const startTime = new Date().toISOString().split('.')[0] + 'Z';
            callWrite({
                operation: EXPLAIN_REVIEW_ADMIT_DC,
                mrn: patientVisit.mrn,
                csn: patientVisit.csn,
                report_date_str: new Date().toISOString().split('.')[0] + 'Z',
            }).then((reviewRevisitResponse) => {
                pollUntilDone({
                    operation: EXPLAIN_REVIEW_ADMIT_DC, 
                    startTime,
                });
            }).catch(() => {
                setIsLoading(false);
            });
        }).catch(() => {
            setIsLoading(false);
        });
    };

    const pollUntilDone = async ({
        operation,
        startTime,
        delayMs = 2000,
        maxAttempts = 60,
    }: any) => {
        if (patientVisit) {
            for (let attempt = 0; attempt < maxAttempts; attempt++) {
                const statuses = await callGetDashboardLLMStatus(patientVisit.csn);
                if (statuses[operation] && statuses[operation].lock_timestamp === null && statuses[operation].last_sync_timestamp > startTime) {
                    const cachedCurrentVisit = await dashboardLLMCache.get(operation, patientVisit, tenant);
                    if (cachedCurrentVisit) {
                        setExplainReviewAdmit(cachedCurrentVisit);
                        markJobAsViewed(EXPLAIN_REVIEW_ADMIT_DC, patientVisit);
                    }

                    const cachedScanData = await dashboardLLMCache.get(SCAN_ADMIT_DC, patientVisit, tenant);
                    if (cachedScanData) {
                        setScanAdmitData(cachedScanData);
                    }
                    setIsLoading(false);
                    setStatuses(statuses);
                    return;
                }

                await sleep(delayMs);
            }
            setIsLoading(false);
            console.error('Polling timed out');
        }
    };

    if (!patientVisit) {
        return <></>
    }


    if (isQuickView && (!scanAdmitData || Object.keys(scanAdmitData).length === 0 || !explainReviewAdmit || Object.keys(explainReviewAdmit).length === 0)) {
        return <></>
    }

    function checkVisitCriteria(): { result: boolean; reason?: string } {
        // Check if target visit has ed_dispo set
        const dispo = patientVisit?.ed_dispo;
        if (!dispo) {
            return { result: false, reason: "Patient visit does not have a disposition status." };
        }
        if (!DISPOSITION_MAPPING[dispo]) {
            return { result: false, reason: "Patient visit has an unknown disposition status." };
        }
        if (DISPOSITION_MAPPING[dispo] === "Discharge") {
            return { result: false, reason: "Patient was not admitted." };
        }

        return { result: true };
    }

    const criteria = checkVisitCriteria();
    if (!isQuickView && !criteria.result) {
        return (
            <Card className="mb-3">
                <Card.Body>
                    <Row className="mb-3">
                        <Col md={12} className="text-left">
                            <div className="d-flex align-items-center">
                                <span className={`dashboard-actions-circle-small dashboard-actions-circle-blue me-2`}><FontAwesomeIcon icon={faHospital} fixedWidth /></span>

                                <strong>Admit Assessment</strong>
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12}>
                            This patient is ineligible for review/assessment - {criteria.reason}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        )
    }

    if (!isLoading && (!explainReviewAdmit || explainReviewAdmit["error"])) {
        return (
            <>
                <GenerateModuleHeroV2 type={EXPLAIN_REVIEW_ADMIT_DC} setRegenerateTarget={() => {}} setShowContextMenu={refresh} />
            </>
        )
    }

    const missingItems = [];

    if (!scanAdmitData) {
        missingItems.push("Admit Data");
    } else {
        if (!scanAdmitData.formatted_notes) {
            missingItems.push("ED Notes");
        }
        if (!scanAdmitData.dc_note) {
            missingItems.push("DC Instructions");
        }
    }

    return (
        <Card className={`fade-in ${className} mb-3`}>
            <FakeLoadingBar isActive={isLoading} maxDuration={EXPECTED_REFRESH_TIME} />
            <Card.Body>
                <Row>
                    <Col md={6}>
                        <div className="d-flex align-items-center">
                            <span className={`dashboard-actions-circle-small dashboard-actions-circle-blue me-2`}><FontAwesomeIcon icon={faHospital} fixedWidth /></span><strong>Admit Assessment</strong> {isSectionUnread(EXPLAIN_REVIEW_ADMIT_DC, unreadFileVersionMap) && <Badge bg="info" className="ms-2">NEW</Badge>} {isSurveyCompleted(feedbackTargetToSurvey) && <Badge bg="success" className="ms-2">COMPLETED</Badge>} {explainReviewAdmit && <NotableLabelsBadge count={getNotableLabels('explain_review_admit_dc', explainReviewAdmit).length} labels={getNotableLabels('explain_review_admit_dc', explainReviewAdmit)} />}
                        </div>
                    </Col>
                    <Col md={6} className="text-right">
                        {patientVisit && <LLMDashboardUpdated patient={patientVisit} statuses={statuses} sectionToSetter={sectionToSetter} latestFileVersions={latestFileVersions} explainKey={EXPLAIN_REVIEW_ADMIT_DC} handleFileVersionChange={handleFileVersionChange} />}

                        <OverlayTrigger
                            placement="bottom"
                            overlay={
                                <Tooltip>
                                    Refresh Section
                                </Tooltip>
                            }
                        >
                            <Button
                                variant="primary"
                                className="dashboard-round-btn"
                                onClick={(e: any) => {
                                    e.stopPropagation();
                                    refresh(true);
                                }}
                                disabled={isLoading}
                            >
                                <FontAwesomeIcon icon={faRefresh} />
                            </Button>
                        </OverlayTrigger>
                        <Button
                            variant="primary"
                            className="dashboard-round-btn"
                            onClick={(e: any) => {e.stopPropagation(); setIsExpanded(!isExpanded); setManuallyChangedIsExpanded(true)}}
                        >
                            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                        </Button>
                    </Col>
                </Row>
                {isLoading && (
                        <div className='mt-2'>
                            <FontAwesomeIcon icon={faCircle} className="pulsating-dot" />
                        </div>
                    )
                }
                {!isLoading && isExpanded && scanAdmitData && explainReviewAdmit && scanAdmitData.formatted_notes && scanAdmitData.dc_note && explainReviewAdmit.summary_ed ? (
                    <Container>
                        {getIsCompleted()}
                        {getContextSection("Provider Note", flattenAndFormatNotes(scanAdmitData.formatted_notes), false)}
                        {getContextSection("Hospital Discharge Summary", scanAdmitData.dc_note?.note_text)}
                        {getSectionToRate("ED Summary", explainReviewAdmit.summary_ed, "",
                            undefined,
                            ['Accurate', 'Partially accurate', 'Inaccurate'], FEEDBACK_TYPE_SUMMARY, false, ED_SUMMARY_OPTION_ALIASES, true,
                            "Capacity Summary", "MD Rating of Capacity Summary")}

                        <Row className="mt-3">
                            <Col>
                                <div className="survey-question-header mb-2">
                                    <div>ED Diagnoses (coded or noted)</div>
                                </div>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                {Array.isArray(explainReviewAdmit.diagnoses_ed) ? (
                                    <div className="survey-context">
                                        <ul>
                                            {explainReviewAdmit.diagnoses_ed.map((item: string, index: number) => (
                                                <li key={index}>{item}</li>
                                            ))}
                                        </ul>
                                    </div>
                                ) : (
                                    <div className="survey-context">
                                        {explainReviewAdmit.diagnoses_ed}
                                    </div>
                                )}
                            </Col>
                        </Row>


                        {getCombinedSectionToRate(
                            handleSave,
                            feedbackTargetToSurvey,
                            "ED Diagnostic Uncertainty",
                            explainReviewAdmit.diagnostic_uncertainty_ed,
                            explainReviewAdmit.diagnostic_uncertainty_justification,
                            false,
                            "Level of diagnostic uncertainty in the ED",
                            ['Low', 'Moderate', 'High'],
                            FEEDBACK_TYPE_DIAGNOSTIC_UNCERTAINTY,
                            "Uncertainty Reasoning",
                            "Reasoning quality:",
                            ['Good', 'Incomplete', 'Poor'],
                            FEEDBACK_TYPE_DIAGNOSTIC_UNCERTAINTY_JUSTIFICATION
                        )}

                        {getSectionToRate(
                            "Admission Summary",
                            explainReviewAdmit.summary_admit,
                            "",
                            undefined,
                            ['Good', 'Incomplete', 'Poor'],
                            FEEDBACK_TYPE_SUMMARY_ADMIT,
                            false,
                            undefined,
                            true,
                            "Capacity Summary",
                            "MD Rating of Capacity Summary"
                        )}

                        {getSectionToRate(
                            "Admission Summary (patient-facing)",
                            explainReviewAdmit.patient_summary_admit,
                            "",
                            undefined,
                            ['Good', 'Incomplete', 'Poor'],
                            FEEDBACK_TYPE_PATIENT_SUMMARY_ADMIT,
                            false,
                            undefined,
                            true,
                            "Capacity Summary",
                            "MD Rating of Capacity Summary"
                        )}

                        {getSectionToRate(
                            "Admission diagnoses (coded or noted)",
                            explainReviewAdmit.diagnoses_admit,
                            "",
                            undefined,
                            ['Good', 'Incomplete', 'Poor'],
                            FEEDBACK_TYPE_DIAGNOSES_ADMIT,
                            false,
                            undefined,
                            true,
                            "Capacity Assessment",
                            "MD Rating of Diagnosis List"
                        )}

                        {getCombinedSectionToRate(
                            handleSave,
                            feedbackTargetToSurvey,
                            "Appropriate Disposition and Level of Care (Prospective)",
                            explainReviewAdmit.appropriate_loc_prospective,
                            explainReviewAdmit.appropriate_loc_prospective_justification,
                            true,
                            "Based only on information available at the time of admission, was the decision to admit to the specified class and level of care medically necessary and appropriate? Consider standard criteria (InterQual/MCG).",
                            [
                                'Appropriate',
                                'Should be higher level of care',
                                'Should be lower level of care',
                                'Should be outpatient',
                                'Should be outpatient with post-discharge monitoring'
                            ],
                            FEEDBACK_TYPE_APPROPRIATE_LOC_PROSPECTIVE,
                            "Prospective Reasoning",
                            "Reasoning quality:",
                            ['Good', 'Incomplete', 'Poor'],
                            FEEDBACK_TYPE_APPROPRIATE_LOC_PROSPECTIVE_JUSTIFICATION,
                            false,
                            undefined,
                            isFieldValueNotable('explain_review_admit_dc', 'appropriate_loc_prospective', explainReviewAdmit.appropriate_loc_prospective)
                        )}

                        {getCombinedSectionToRate(
                            handleSave,
                            feedbackTargetToSurvey,
                            "Appropriate Disposition and Level of Care (Retrospective)",
                            explainReviewAdmit.appropriate_loc_retrospective,
                            explainReviewAdmit.appropriate_loc_retrospective_justification,
                            true,
                            "Considering the full hospital course, re-evaluate the initial admission decision. Did the patient's clinical evolution, final diagnoses, and procedures justify the initial level of care?",
                            [
                                'Appropriate',
                                'Should be higher level of care',
                                'Should be lower level of care',
                                'Should be outpatient',
                                'Should be outpatient with post-discharge monitoring'
                            ],
                            FEEDBACK_TYPE_APPROPRIATE_LOC_RETROSPECTIVE,
                            "Retrospective Reasoning",
                            "Reasoning quality:",
                            ['Good', 'Incomplete', 'Poor'],
                            FEEDBACK_TYPE_APPROPRIATE_LOC_RETROSPECTIVE_JUSTIFICATION,
                            false,
                            undefined,
                            isFieldValueNotable('explain_review_admit_dc', 'appropriate_loc_retrospective', explainReviewAdmit.appropriate_loc_retrospective)
                        )}

                        {getCombinedSectionToRate(
                            handleSave,
                            feedbackTargetToSurvey,
                            "Significant Findings",
                            explainReviewAdmit.significant_findings,
                            explainReviewAdmit.significant_findings_justification,
                            false,
                            "Were there critical diagnoses, complications, deteriorations, or findings made during the hospitalization that were not apparent or strongly suspected at the time of ED evaluation, and that significantly altered the patient's management or prognosis?",
                            ['Yes', 'Possibly', 'No'],
                            FEEDBACK_TYPE_SIGNIFICANT_FINDINGS,
                            "Significant Findings Reasoning",
                            "Reasoning quality:",
                            ['Good', 'Incomplete', 'Poor'],
                            FEEDBACK_TYPE_SIGNIFICANT_FINDINGS_JUSTIFICATION,
                            false,
                            undefined,
                            isFieldValueNotable('explain_review_admit_dc', 'significant_findings', explainReviewAdmit.significant_findings)
                        )}

                        {getCombinedSectionToRate(
                            handleSave,
                            feedbackTargetToSurvey,
                            "Diagnostic Clarity",
                            explainReviewAdmit.diagnostic_clarity,
                            explainReviewAdmit.diagnostic_clarity_justification,
                            true,
                            "How much did the diagnostic picture evolve from presentation to discharge? This assesses initial uncertainty and the value of the inpatient workup.",
                            ['ED diagnosis confirmed', 'ED diagnosis refined', 'ED diagnosis changed'],
                            FEEDBACK_TYPE_DIAGNOSTIC_CLARITY,
                            "Diagnostic Clarity Reasoning",
                            "Reasoning quality:",
                            ['Good', 'Incomplete', 'Poor'],
                            FEEDBACK_TYPE_DIAGNOSTIC_CLARITY_JUSTIFICATION,
                            false,
                            undefined,
                            isFieldValueNotable('explain_review_admit_dc', 'diagnostic_clarity', explainReviewAdmit.diagnostic_clarity)
                        )}

                        {getCombinedSectionToRate(
                            handleSave,
                            feedbackTargetToSurvey,
                            "Value of Stay",
                            explainReviewAdmit.value_of_stay,
                            explainReviewAdmit.value_of_stay_justification,
                            true,
                            "Categories of care value during the hospital stay",
                            [
                                'Life Saving Intervention',
                                'Appropriate Inpatient Procedure',
                                'Critical Diagnostic Clarification',
                                'Symptom Management',
                                'Supportive Care',
                                'Social or Placement Need',
                            ],
                            FEEDBACK_TYPE_VALUE_OF_STAY,
                            "Value of Stay Justification",
                            "Justify value of stay rating:",
                            ['Good', 'Incomplete', 'Poor'],
                            FEEDBACK_TYPE_VALUE_OF_STAY_JUSTIFICATION,
                            true,
                            {
                                'life_saving_intervention': 'Life Saving Intervention',
                                'appropriate_inpatient_procedure': 'Appropriate Inpatient Procedure',
                                'critical_diagnostic_clarification': 'Critical Diagnostic Clarification',
                                'symptom_management': 'Symptom Management',
                                'supportive_care': 'Supportive Care',
                                'social_or_placement_need': 'Social or Placement Need',
                            }
                        )}

                        {getCombinedSectionToRate(
                            handleSave,
                            feedbackTargetToSurvey,
                            "Modifiable Admission",
                            explainReviewAdmit.modifiable_admission,
                            explainReviewAdmit.modifiable_admission_justification,
                            true,
                            "Based on the entire clinical picture, could specific actions or processes, if done differently in the ED or a robust outpatient setting, have prevented this admission or allowed for a lower/more resource-efficient level of care?",
                            [
                                'Not modifiable',
                                'Modifiable with outpatient care',
                                'Modifiable with urgent (<24h) outpatient care',
                                'Modifiable with post-discharge monitoring',
                                'Modifiable with better ED workup/management',
                                'Modifiable by addressing social determinants of health',
                                'Other (explain)',
                            ],
                            FEEDBACK_TYPE_MODIFIABLE_ADMISSION,
                            "Modifiability Reasoning",
                            "Reasoning quality:",
                            ['Good', 'Incomplete', 'Poor'],
                            FEEDBACK_TYPE_MODIFIABLE_ADMISSION_JUSTIFICATION,
                            true,
                            undefined,
                            isFieldValueNotable('explain_review_admit_dc', 'modifiable_admission', explainReviewAdmit.modifiable_admission)
                        )}

                        {getCombinedSectionToRate(
                            handleSave,
                            feedbackTargetToSurvey,
                            "CDS Opportunity",
                            explainReviewAdmit.cds_opportunity,
                            explainReviewAdmit.cds_opportunity_detail,
                            false,
                            "Could the admission have been prevented/improved with ED clinical decision support?",
                            ['Yes', 'No', 'Possibly'],
                            FEEDBACK_TYPE_CDS_OPPORTUNITY,
                            "CDS Reasoning",
                            "Reasoning quality:",
                            ['Good', 'Incomplete', 'Poor'],
                            FEEDBACK_TYPE_CDS_OPPORTUNITY_DETAIL
                        )}

                        {getCombinedSectionToRate(
                            handleSave,
                            feedbackTargetToSurvey,
                            "Admission Streamlining Opportunities",
                            explainReviewAdmit.streamline_admission,
                            explainReviewAdmit.streamline_admission_justification,
                            true,
                            "Did the inpatient stay involve any potentially avoidable delays or inefficiencies that prolonged the length of stay without clear medical benefit? Consider delays in testing, consultations, or treatments.",
                            ['No', 'Delayed testing', 'Delayed consultation', 'Delayed treatment', 'Other (please explain)'],
                            FEEDBACK_TYPE_STREAMLINE_ADMISSION,
                            "Admission Efficiency Reasoning",
                            "Reasoning quality:",
                            ['Good', 'Incomplete', 'Poor'],
                            FEEDBACK_TYPE_STREAMLINE_ADMISSION_JUSTIFICATION,
                            true
                        )}

                        {getSectionToRate(
                            "Longitudinal Care Insight (Admit)",
                            explainReviewAdmit.longitudinal_care_insight_admit,
                            "",
                            "A clinical pearl learned about the patient that should be available to the next provider to improve future care efficiency and personalization.",
                            ['Good', 'Incomplete', 'Poor'],
                            FEEDBACK_TYPE_LONGITUDINAL_CARE_INSIGHT_ADMIT,
                            false,
                            undefined,
                            true,
                            "Capacity Assessment",
                            "MD Rating of Capacity Assessment"
                        )}

                        {getCombinedSectionToRate(
                            handleSave,
                            feedbackTargetToSurvey,
                            "Therapeutic Pivot (ED)",
                            explainReviewAdmit.therapeutic_pivot_ed,
                            explainReviewAdmit.therapeutic_pivot_justification_ed,
                            false,
                            "Was there a significant change in the ED treatment plan based on new data or a change in the patient's condition?",
                            ['Yes', 'Possibly', 'No'],
                            FEEDBACK_TYPE_THERAPEUTIC_PIVOT_ED,
                            "Therapeutic Pivot Justification (ED)",
                            "Reasoning quality",
                            ['Good', 'Incomplete', 'Poor'],
                            FEEDBACK_TYPE_THERAPEUTIC_PIVOT_JUSTIFICATION_ED
                        )}

                        {getCombinedSectionToRate(
                            handleSave,
                            feedbackTargetToSurvey,
                            "Value of Care (ED)",
                            explainReviewAdmit.value_of_care_ed,
                            explainReviewAdmit.value_of_care_justification_ed,
                            false,
                            "Assess if the ED encounter added clinical value.",
                            ['High', 'Moderate', 'Low', 'Negative'],
                            FEEDBACK_TYPE_VALUE_OF_CARE_ED,
                            "Value of Care Justification (ED)",
                            "Reasoning quality",
                            ['Good', 'Incomplete', 'Poor'],
                            FEEDBACK_TYPE_VALUE_OF_CARE_JUSTIFICATION_ED
                        )}

                        {getCombinedSectionToRate(
                            handleSave,
                            feedbackTargetToSurvey,
                            "Therapeutic Pivot (Inpatient)",
                            explainReviewAdmit.therapeutic_pivot_ip,
                            explainReviewAdmit.therapeutic_pivot_justification_ip,
                            false,
                            "Was there a significant change in the inpatient treatment plan based on new data or a change in the patient's condition?",
                            ['Yes', 'No'],
                            FEEDBACK_TYPE_THERAPEUTIC_PIVOT_IP,
                            "Therapeutic Pivot Justification (Inpatient)",
                            "Reasoning quality",
                            ['Good', 'Incomplete', 'Poor'],
                            FEEDBACK_TYPE_THERAPEUTIC_PIVOT_JUSTIFICATION_IP,
                            false,
                            undefined,
                            isFieldValueNotable('explain_review_admit_dc', 'therapeutic_pivot_ip', explainReviewAdmit.therapeutic_pivot_ip)
                        )}

                        {getCombinedSectionToRate(
                            handleSave,
                            feedbackTargetToSurvey,
                            "Value of Care (Inpatient)",
                            explainReviewAdmit.value_of_care_ip,
                            explainReviewAdmit.value_of_care_justification_ip,
                            false,
                            "Assess the clinical value of the inpatient stay.",
                            ['High', 'Moderate', 'Low', 'Negative'],
                            FEEDBACK_TYPE_VALUE_OF_CARE_IP,
                            "Value of Care Justification (Inpatient)",
                            "Reasoning quality",
                            ['Good', 'Incomplete', 'Poor'],
                            FEEDBACK_TYPE_VALUE_OF_CARE_JUSTIFICATION_IP,
                            false,
                            undefined,
                            isFieldValueNotable('explain_review_admit_dc', 'value_of_care_ip', explainReviewAdmit.value_of_care_ip)
                        )}

                        {getCombinedSectionToRate(
                            handleSave,
                            feedbackTargetToSurvey,
                            "Astute Diagnosis (Admit)",
                            explainReviewAdmit.astute_diagnosis_admit,
                            explainReviewAdmit.astute_diagnosis_justification_admit,
                            false,
                            "Was a correct and high-impact diagnosis made that was not obvious or routine, given the patient's initial presentation? An astute diagnosis often involves recognizing an atypical presentation of a common disease or identifying a rare condition.",
                            ['Yes', 'Possibly', 'No'],
                            FEEDBACK_TYPE_ASTUTE_DIAGNOSIS_ADMIT,
                            "Astute Diagnosis Justification (Admit)",
                            "Reasoning quality",
                            ['Good', 'Incomplete', 'Poor'],
                            FEEDBACK_TYPE_ASTUTE_DIAGNOSIS_JUSTIFICATION_ADMIT
                        )}

                    </Container>
                ) : !isLoading && isExpanded && missingItems.length > 0 ? (
                    <>We couldn't generate an assessment because the following information is missing: <strong>{missingItems.join(", ")}</strong></>
                ) : !isLoading && isExpanded && scanAdmitData && explainReviewAdmit ? (
                    <>We're sorry - we could not generate an assessment due to an error</>
                ) : (<></>)}
            </Card.Body>
        </Card>
    );
};
